/** The payer context of a booking episode, asked per booking (never derived). */
export var PatientBookingPayerContext;
(function (PatientBookingPayerContext) {
    /** Gesetzlich versichert. Stubbed in the patient flow until the GKV slices. */
    PatientBookingPayerContext["GKV"] = "gkv";
    /** Privat versichert. */
    PatientBookingPayerContext["PKV"] = "pkv";
    /** Selbstzahler. */
    PatientBookingPayerContext["SELF_PAY"] = "self_pay";
})(PatientBookingPayerContext || (PatientBookingPayerContext = {}));
/** Discriminator marking an `intakePayload` as a first-contact booking snapshot. */
export const FIRST_CONTACT_INTAKE_KIND = 'first-contact';
/**
 * The standard length (minutes) a first-contact hold blocks. A fresh episode
 * has no plan continuity to mirror a duration from, and treatment concepts carry
 * no default duration, so the hold blocks a standard slot; the practice sets the
 * real duration when it creates the plan and can move the appointment at confirm.
 */
export const PATIENT_BOOKING_FIRST_CONTACT_DURATION_MIN = 30;
/**
 * Build the intake payload for a non-GKV treatment-select first-contact booking
 * (RS-008). The patient picked a payer context and one treatment; counts and
 * frequency are optional and omitted here (a non-GKV episode books one slot).
 */
export function buildTreatmentSelectIntake(input) {
    return {
        kind: FIRST_CONTACT_INTAKE_KIND,
        payerContext: input.payerContext,
        treatments: [
            {
                treatmentConceptId: input.treatmentConceptId,
                treatmentName: input.treatmentName,
                ...(input.count != null ? { count: input.count } : {}),
            },
        ],
    };
}
/**
 * Build the intake payload for a manual GKV first-contact booking (RS-009). The
 * patient typed the structured fields printed on the Muster 13 — one or more
 * Heilmittel with their Anzahl, the weekly Therapiefrequenz and the urgency
 * flag — with no free-text field anywhere. The barcode scan (RS-010) emits this
 * exact shape, so the practice-side confirm renders both fill methods
 * identically. The primary treatment (drives the slot search + duration) is the
 * first entry in {@link treatments}.
 */
export function buildGkvManualIntake(input) {
    return {
        kind: FIRST_CONTACT_INTAKE_KIND,
        payerContext: PatientBookingPayerContext.GKV,
        treatments: input.treatments.map((t) => ({
            treatmentConceptId: t.treatmentConceptId,
            treatmentName: t.treatmentName,
            ...(t.count != null ? { count: t.count } : {}),
        })),
        frequency: input.frequency ?? null,
        urgentTreatmentNeeded: input.urgentTreatmentNeeded ?? false,
    };
}
/**
 * True when an `intakePayload` is a first-contact booking snapshot. The practice
 * inbox routes these through the prefilled plan form (they carry no plan and
 * must not be one-click confirmed).
 */
export function isFirstContactIntake(payload) {
    return (!!payload &&
        payload.kind === FIRST_CONTACT_INTAKE_KIND &&
        Array.isArray(payload.treatments));
}
/** Marker key on `intakePayload` referencing the Folgeverordnung's predecessor plan. */
export const FOLGEVERORDNUNG_PREDECESSOR_INTAKE_KEY = 'predecessorPlanId';
/**
 * The intake-payload fragment stamped on a Folgeverordnung hold (RS-011),
 * merged onto the same object as the GKV intake so the shape stays one shape.
 */
export function folgeverordnungIntakeMarker(predecessorPlanId) {
    return { [FOLGEVERORDNUNG_PREDECESSOR_INTAKE_KEY]: predecessorPlanId };
}
/** The predecessor plan id of a Folgeverordnung hold, or null when it is not one. */
export function folgeverordnungPredecessorId(payload) {
    if (!isFirstContactIntake(payload))
        return null;
    const id = payload[FOLGEVERORDNUNG_PREDECESSOR_INTAKE_KEY];
    return typeof id === 'string' && id.length > 0 ? id : null;
}
/** Marker key on `intakePayload` recording that a hold came from the website widget. */
export const WEBSITE_BOOKING_INTAKE_KEY = 'websiteBooking';
/** Sub-key under {@link WEBSITE_BOOKING_INTAKE_KEY}'s object holding the contact. */
export const WEBSITE_BOOKING_CONTACT_KEY = 'contact';
/**
 * The intake-payload fragment stamped on a website first-contact hold, merged
 * onto the SAME first-contact object as the treatment intake (one shape). Marks
 * the hold as website-originated (so the inbox badges it "von der Website") and
 * carries the visitor's contact off the person row. There is NO new column and
 * NO new origin enum value — the marker rides the existing `intakePayload` JSON.
 */
export function websiteBookingIntakeMarker(contact) {
    return {
        [WEBSITE_BOOKING_INTAKE_KEY]: true,
        [WEBSITE_BOOKING_CONTACT_KEY]: {
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phone: contact.phone,
        },
    };
}
/** True when a hold's intake payload records it came from the website widget. */
export function isWebsiteBookingIntake(payload) {
    return !!payload && payload[WEBSITE_BOOKING_INTAKE_KEY] === true;
}
/** The website visitor's contact from a hold's intake payload, or null. */
export function websiteBookingContact(payload) {
    if (!isWebsiteBookingIntake(payload))
        return null;
    const contact = payload[WEBSITE_BOOKING_CONTACT_KEY];
    if (!contact || typeof contact !== 'object')
        return null;
    const c = contact;
    if (typeof c.firstName === 'string' &&
        typeof c.lastName === 'string' &&
        typeof c.email === 'string' &&
        typeof c.phone === 'string') {
        return { firstName: c.firstName, lastName: c.lastName, email: c.email, phone: c.phone };
    }
    return null;
}
/**
 * Marker key on `intakePayload` recording that the practice dismissed the
 * duplicate-person suggestion for this hold (PRD #1358, RS-008). Dismissing is
 * one action and it must not reappear for that hold, so the decision is
 * persisted on the same intake payload rather than held in the UI — reopening
 * the hold later stays dismissed.
 */
export const DUPLICATE_MATCHES_DISMISSED_KEY = 'duplicateMatchesDismissed';
/** The intake-payload fragment stamped when the practice dismisses the duplicate hint. */
export function duplicateMatchesDismissedMarker() {
    return { [DUPLICATE_MATCHES_DISMISSED_KEY]: true };
}
/** True when the practice has dismissed the duplicate-person hint for this hold. */
export function isDuplicateMatchesDismissed(payload) {
    return !!payload && payload[DUPLICATE_MATCHES_DISMISSED_KEY] === true;
}
/**
 * The DSGVO consent shown before a website booking is submitted (decided on
 * #1361, drafted for DPO confirmation). It is used VERBATIM — the widget renders
 * this exact string and a test asserts it character-for-character. Do NOT
 * reword, "improve" or translate it.
 */
export const EMBED_BOOKING_CONSENT_TEXT = 'Mit dem Absenden erklären Sie sich damit einverstanden, dass die Praxis die angegebenen Daten — einschließlich der Angaben zu Ihrer Verordnung — zur Bearbeitung Ihrer Terminanfrage verarbeitet. Therame verarbeitet diese Daten im Auftrag der Praxis.';
//# sourceMappingURL=booking-intake.js.map